import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { X, Loader, Search, User, AlertTriangle, Calendar } from 'lucide-react';

const CreateComplaintModal = ({ onClose, onSave, preselectedCustomer }) => {
  const [formData, setFormData] = useState({
    customer: preselectedCustomer?.id || '',
    title: '',
    description: '',
    priority: 'medium',
    technician: '',
    complaint_date: new Date().toISOString().split('T')[0],
    due_date: '',
  });
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);

  // Customer search states
  const [customerSearch, setCustomerSearch] = useState(preselectedCustomer?.username || '');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(preselectedCustomer || null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const techRes = await api.get('/technicians/all-technicians/');
        setTechnicians(techRes.data || []);
      } catch (err) {
        console.error(err);
        setError('Failed to load technicians');
      } finally {
        setFetchingData(false);
      }
    };
    fetchData();
  }, []);

  // Customer search with debounce
  useEffect(() => {
    if (customerSearch.length < 2) {
      setCustomerResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.get(`/customers/?search=${encodeURIComponent(customerSearch)}`);
        setCustomerResults(res.data.results || []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({ ...prev, customer: customer.id }));
    setCustomerSearch(customer.username);
    setCustomerResults([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer) {
      setError('Please select a customer');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        technician: formData.technician ? parseInt(formData.technician) : null,
        due_date: formData.due_date || null,
      };
      await api.post('/complaints/', payload);
      onSave();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      let msg = 'Error creating complaint';
      if (data) {
        const fieldErrors = Object.entries(data)
          .filter(([k]) => k !== 'detail')
          .map(([field, msgs]) => `${field}: ${(Array.isArray(msgs) ? msgs : [msgs]).join(', ')}`);
        msg = fieldErrors.length ? fieldErrors.join('; ')
          : data.detail || data.error || msg;
      }
      setError(msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm disabled:bg-gray-50";
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide";

  if (fetchingData) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 flex items-center gap-3">
          <Loader className="animate-spin text-blue-600" size={24} />
          <span className="text-gray-700 font-medium">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create Complaint</h2>
            <p className="text-xs text-gray-500 mt-1">Log a new customer complaint and assign a technician</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <span className="text-red-600 text-sm font-medium">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Customer Search - Full Width */}
            <div className="md:col-span-2 relative">
              <label className={labelClass}><User size={12} className="inline mr-1" /> Customer *</label>
              {selectedCustomer ? (
                <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex-1">
                    <span className="font-medium text-sm text-blue-900">{selectedCustomer.username}</span>
                    {selectedCustomer.name && <span className="text-xs text-blue-700 ml-2">({selectedCustomer.name})</span>}
                  </div>
                  <button type="button" onClick={() => { setSelectedCustomer(null); setFormData(prev => ({ ...prev, customer: '' })); setCustomerSearch(''); }} className="text-blue-600 hover:text-blue-800">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text" value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setFormData(prev => ({ ...prev, customer: '' })); }}
                      placeholder="Search by username, name, or phone..."
                      className={`${inputClass} pl-9`}
                    />
                  </div>
                  {customerResults.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {customerResults.map(c => (
                        <button key={c.id} type="button" onClick={() => selectCustomer(c)} className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0">
                          <span className="font-medium text-sm">{c.username}</span>
                          {c.name && <span className="text-xs text-gray-500 ml-2">{c.name}</span>}
                          {c.area_name && <span className="text-xs text-gray-400 ml-2">({c.area_name})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {searchLoading && <p className="text-xs text-gray-500 mt-1">Searching...</p>}
                </>
              )}
            </div>

            {/* Title */}
            <div className="md:col-span-2">
              <label className={labelClass}><AlertTriangle size={12} className="inline mr-1" /> Title *</label>
              <input
                type="text" name="title" value={formData.title} onChange={handleChange}
                required className={inputClass} placeholder="Brief complaint title"
              />
            </div>

            {/* Priority */}
            <div>
              <label className={labelClass}>Priority *</label>
              <select name="priority" value={formData.priority} onChange={handleChange} className={inputClass}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            {/* Technician */}
            <div>
              <label className={labelClass}>Assign Technician</label>
              <select name="technician" value={formData.technician} onChange={handleChange} className={inputClass}>
                <option value="">Unassigned</option>
                {technicians.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.username})</option>
                ))}
              </select>
            </div>

            {/* Complaint Date */}
            <div>
              <label className={labelClass}><Calendar size={12} className="inline mr-1" /> Complaint Date *</label>
              <input
                type="date" name="complaint_date" value={formData.complaint_date} onChange={handleChange}
                required className={inputClass}
              />
            </div>

            {/* Due Date */}
            <div>
              <label className={labelClass}><Calendar size={12} className="inline mr-1" /> Due Date</label>
              <input
                type="date" name="due_date" value={formData.due_date} onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Description - Full Width */}
            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <textarea
                name="description" value={formData.description} onChange={handleChange}
                rows="3" className={inputClass} placeholder="Detailed complaint description..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-6 mt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading || !formData.customer}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-md shadow-blue-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? <><Loader size={18} className="animate-spin" /> <span>Creating...</span></> : 'Create Complaint'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateComplaintModal;
